import React from 'react';
import Modal from '../../../../components/Modal';

/**
 * MeetingBookingModal - Modal för att välja mötestider
 * Används när kund vill föreslå 2-3 tidsförslag för uppföljningsmöte
 *
 * @param {boolean} isOpen - Om modalen är öppen
 * @param {function} onClose - Callback när modalen stängs
 * @param {array} meetingSlots - Array med valda tidsslots
 * @param {function} onSlotChange - Callback när användaren väljer/avväljer ett slot
 * @param {array} availableSlots - Tillgängliga tidsförslag att välja från
 * @param {number} maxSlots - Max antal slots som kan väljas (default: 3)
 * @param {number} minSlots - Min antal slots som krävs (default: 2)
 * @param {string} mode - 'select' (bara välj tider) eller 'submit' (skicka direkt)
 * @param {function} onSubmit - Callback för direktskickning (endast i 'submit' mode)
 * @param {boolean} submitting - Om formuläret håller på att skickas
 */
const MeetingBookingModal = ({
  isOpen,
  onClose,
  meetingSlots = [],
  onSlotChange,
  availableSlots = [],
  maxSlots = 3,
  minSlots = 2,
  mode = 'select',
  onSubmit,
  submitting = false,
}) => {
  const handleSlotToggle = (slot, isChecked) => {
    if (isChecked) {
      // Lägg till slot om det inte överskrider max
      if (meetingSlots.length >= maxSlots) return;
      onSlotChange([...meetingSlots, slot]);
    } else {
      // Ta bort slot
      onSlotChange(meetingSlots.filter(s => s !== slot));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-2">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Välj tider ({minSlots}–{maxSlots} förslag)
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          Välj {minSlots}–{maxSlots} tider som passar er. Förslagen visas ons–fre kl. 11–12 eller 14–15 de kommande två veckorna.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-auto border rounded p-2">
          {availableSlots.map((slot) => {
            const selected = meetingSlots.includes(slot);
            return (
              <label 
                key={slot} 
                className={`flex items-center gap-2 text-sm px-2 py-2 rounded border cursor-pointer transition-colors ${
                  selected 
                    ? 'bg-green-50 border-green-500' 
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => handleSlotToggle(slot, e.target.checked)}
                  className="cursor-pointer"
                />
                <span className="text-gray-800">{slot}</span>
              </label>
            );
          })}
        </div>
        {mode === 'select' && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            <strong>OBS:</strong> När du har valt tider, klicka på "Skicka feedback" för att skicka dina tidsförslag till byggkonsulten.
          </div>
        )}
        <div className="flex items-center justify-between mt-3 text-xs text-gray-600">
          <span>
            Valda: {meetingSlots.length} / {maxSlots}
            {meetingSlots.length < minSlots && (
              <span className="text-orange-600 ml-2">
                (Välj minst {minSlots - meetingSlots.length} till)
              </span>
            )}
          </span>
          <button
            type="button"
            className={`px-4 py-2 rounded text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              mode === 'submit' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-leadme-gray-dark hover:bg-leadme-gray'
            }`}
            onClick={() => {
              if (mode === 'submit' && onSubmit) {
                onSubmit(meetingSlots);
              } else {
                onClose();
              }
            }}
            disabled={meetingSlots.length < minSlots || submitting}
          >
            {submitting ? 'Skickar...' : (mode === 'submit' ? 'Skicka förslag' : 'Välj')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MeetingBookingModal;

